import type { PayloadAction } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { GetTasksPayload } from '@/domains/tasks/store/types';
import type { RootState } from '@/store/types/RootState';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { TaskListItemSlots } from '@/domains/tasks/features/myTaskList/enums/TaskListItemSlots';
import { getTasks, getTasksFailed, getTasksSuccess, incrementTasksPage } from '@/domains/tasks/store/tasksSlice';
import { SortDirection } from '@/enums/SortDirection';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import RecordService from '@/services/RecordService/RecordService';

function* getTasksSaga({ type, payload }: PayloadAction<GetTasksPayload>) {
  try {
    const pagination = yield select((state: RootState) => state.newTasksSlice.data.pagination);

    const sortBy = payload?.sorting?.propertyName || 'TaskStatusId';
    const sortDirection = payload?.sorting?.sortDirection || SortDirection.ASC;

    const requestUrl = `/filter/${EdsEntityCodes.Tasks}?${QueryParamsService.constructSearchParamsFromRecord({
      page: pagination.page,
      perPage: pagination.perPage,
      sortBy,
      sortDirection,
      includes: `${EdsEntityCodes.AssignmentQueue}.${EdsEntityCodes.AssignmentQueueMemberships}.${EdsEntityCodes.Person},${EdsEntityCodes.Assignee}.${EdsEntityCodes.Accounts},${EdsEntityCodes.HelperTags}`,
    })}`;

    const { data } = yield call(HttpService.post, requestUrl, payload?.filters || [], {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      includeId: true,
      section: payload?.section,
    });

    const tasksWithoutAvailableHelperTags = normalizedResponse.map((normalizedTask) => {
      return {
        ...normalizedTask,
        entities: {
          ...RecordService.omit([EdsEntityCodes.AvailableHelperTags], normalizedTask.entities),
        },
      };
    });

    EdsDebugService.logResponse(EdsEntityCodes.Tasks, {
      section: payload?.section,
      rawResponse: data,
      normalizedResponse,
      dataBySlot: normalizedResponse.length
        ? EdsAdapterService.generateSlotRecord<TaskListItemSlots>(normalizedResponse[0])
        : {},
    });

    yield put(
      getTasksSuccess({
        shouldRewrite: type !== incrementTasksPage.type,
        data: {
          listings: tasksWithoutAvailableHelperTags,
          pagination: {
            ...data.pagination,
            isLastPage: data.pagination.page * data.pagination.perPage >= data.pagination.numberOfItems,
          },
        },
      }),
    );
  } catch (error) {
    console.error(error);
    yield put(getTasksFailed());
  }
}

export function* tasksWatcher() {
  yield takeLatest([getTasks.type, incrementTasksPage.type], getTasksSaga);
}
